import { getChunks } from "@codemirror/merge";
import { EditorSelection } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

const findDeletionBlockAt = (view: EditorView, pos: number) => {
  const isWidgetBlock = (b: ReturnType<EditorView["lineBlockAt"]>) =>
    !Array.isArray(b.type) && b.widget != null;

  const at = view.lineBlockAt(pos);
  if (isWidgetBlock(at)) return at;

  if (pos > 0) {
    const before = view.lineBlockAt(pos - 1);
    if (isWidgetBlock(before)) return before;
  }

  for (const b of view.viewportLineBlocks) {
    // Prefer exact anchor match when available
    if (!Array.isArray(b.type) && b.widget && b.from === pos) return b;
  }
  return null;
};

const scrollDeletionTopIntoView = (view: EditorView, pos: number): void => {
  view.requestMeasure({
    key: "scrollDeletionTopIntoView",
    read: v => {
      const widgetBlock = findDeletionBlockAt(v, pos);
      const top = (widgetBlock ?? v.lineBlockAt(pos)).top;
      return { top };
    },
    write: ({ top }, v) => {
      const y = Math.max(0, top);
      const scroller = v.scrollDOM;
      if (typeof scroller.scrollTo === "function") {
        scroller.scrollTo({ top: y, behavior: "smooth" });
      } else {
        scroller.scrollTop = y;
      }
    },
  });
};

export const navigateToChunk = (view: EditorView, dir: 1 | -1): void => {
  const chunksInfo = getChunks(view.state);
  const chunks = chunksInfo?.chunks ?? [];
  if (!chunks.length) return;

  const head = view.state.selection.main.head;

  let pos = 0;
  for (let i = chunks.length - 1; i >= 0; i--) {
    const c = chunks[i];
    const from = c.fromB;
    const to = c.toB;
    if (to < head) {
      pos = i + 1;
      break;
    }
    if (from <= head) {
      pos = i + (dir < 0 ? 0 : 1);
      break;
    }
  }
  const next =
    chunks[(pos + (dir < 0 ? chunks.length - 1 : 0)) % chunks.length];
  const targetFrom = next.fromB;

  view.dispatch({
    selection: EditorSelection.cursor(targetFrom),
    userEvent: "select.byChunk",
  });

  scrollDeletionTopIntoView(view, targetFrom);
};
